import { implementCustomExpressions, implementCustomInvariants } from '../__generated/implementation.js';
import { CabinetColor } from '../__generated/model/index.js';
import { MaterialSwatchMap, MaterialsMap, StainlessMaterialProperties } from '../data/cabinets.js';
import { recipes } from '../data/recipes.js';
import { Model, round } from '../index.js';
import { isDealer } from '../util/auth.js';
const defaultMaterialMap = MaterialsMap[CabinetColor.DesertSage];
const WALL_MAX = 600 * 25.4;
const CAMERA_MIN = 6000;
const CAMERA_MAX = 60000;
const CAMERA_FAR_MIN = 20000;
const CAMERA_FAR_MAX = 80000;
const getCabinetMaterialProperties = (powderCoatType, color)=>{
    if (powderCoatType === Model.PowderCoatType.Pulse) {
        return MaterialsMap[color] ?? defaultMaterialMap;
    } else {
        return StainlessMaterialProperties;
    }
};
const getFrontPanelMaterialProperties = (powderCoatType, color)=>{
    if (powderCoatType === Model.PowderCoatType.Anvil) {
        return StainlessMaterialProperties;
    } else {
        return MaterialsMap[color] ?? defaultMaterialMap;
    }
};
export const roomExpressions = implementCustomExpressions({
    Room: {
        $wallA: (s)=>s.$walls.find((w)=>w.wallType === 'A'),
        $wallB: (s)=>s.$walls.find((w)=>w.wallType === 'B'),
        $wallC: (s)=>s.$walls.find((w)=>w.wallType === 'C'),
        $cabinetColor: (s)=>getCabinetMaterialProperties(s.powderCoatType, s.cabinetColor).color,
        $cabinetMetalness: (s)=>getCabinetMaterialProperties(s.powderCoatType, s.cabinetColor).metalness,
        $cabinetReflectivity: (s)=>getCabinetMaterialProperties(s.powderCoatType, s.cabinetColor).reflectivity,
        $cabinetClearcoat: (s)=>getCabinetMaterialProperties(s.powderCoatType, s.cabinetColor).clearcoat,
        $cabinetClearcoatRoughness: (s)=>getCabinetMaterialProperties(s.powderCoatType, s.cabinetColor).clearcoatRoughness,
        $cabinetRoughness: (s)=>getCabinetMaterialProperties(s.powderCoatType, s.cabinetColor).roughness,
        $cabinetRoughnessMapUrl: (s)=>getCabinetMaterialProperties(s.powderCoatType, s.cabinetColor).roughnessMapUrl,
        $cabinetNormalMapUrl: (s)=>getCabinetMaterialProperties(s.powderCoatType, s.cabinetColor).normalMapUrl,
        $cabinetNormalScale: (s)=>getCabinetMaterialProperties(s.powderCoatType, s.cabinetColor).normalScale,
        $cabinetRepeatX: (s)=>getCabinetMaterialProperties(s.powderCoatType, s.cabinetColor).repeatX,
        $cabinetRepeatY: (s)=>getCabinetMaterialProperties(s.powderCoatType, s.cabinetColor).repeatY,
        $frontPanelColor: (s)=>getFrontPanelMaterialProperties(s.powderCoatType, s.cabinetColor).color,
        $frontPanelMetalness: (s)=>getFrontPanelMaterialProperties(s.powderCoatType, s.cabinetColor).metalness,
        $frontPanelReflectivity: (s)=>getFrontPanelMaterialProperties(s.powderCoatType, s.cabinetColor).reflectivity,
        $frontPanelClearcoat: (s)=>getFrontPanelMaterialProperties(s.powderCoatType, s.cabinetColor).clearcoat,
        $frontPanelClearcoatRoughness: (s)=>getFrontPanelMaterialProperties(s.powderCoatType, s.cabinetColor).clearcoatRoughness,
        $frontPanelRoughness: (s)=>getFrontPanelMaterialProperties(s.powderCoatType, s.cabinetColor).roughness,
        $frontPanelRoughnessMapUrl: (s)=>getFrontPanelMaterialProperties(s.powderCoatType, s.cabinetColor).roughnessMapUrl,
        $frontPanelNormalMapUrl: (s)=>getFrontPanelMaterialProperties(s.powderCoatType, s.cabinetColor).normalMapUrl,
        $frontPanelNormalScale: (s)=>getFrontPanelMaterialProperties(s.powderCoatType, s.cabinetColor).normalScale,
        $frontPanelRepeatX: (s)=>getFrontPanelMaterialProperties(s.powderCoatType, s.cabinetColor).repeatX,
        $frontPanelRepeatY: (s)=>getFrontPanelMaterialProperties(s.powderCoatType, s.cabinetColor).repeatY,
        $cameraNear: ()=>750,
        $cameraFar: (s)=>s.$maxSideLength / WALL_MAX * (CAMERA_FAR_MAX - CAMERA_FAR_MIN) + CAMERA_FAR_MIN,
        $cameraMinDistance: ()=>2000,
        $cameraMaxDistance: (s)=>s.$maxSideLength / WALL_MAX * (CAMERA_MAX - CAMERA_MIN) + CAMERA_MIN,
        // Offset - dirty fix for uncentered room.
        $boundingBoxMinX: (s)=>-s.$width / 2 + (s.shape === 'I' ? 800 : 0),
        $boundingBoxMaxX: (s)=>s.$width / 2 + (s.shape === 'I' ? 800 : 0),
        $boundingBoxMinY: ()=>0,
        $boundingBoxMaxY: (s)=>s.$wallB.$fullHeight,
        $boundingBoxMinZ: (s)=>s.shape === 'I' ? 0 : -s.$depth / 2,
        $boundingBoxMaxZ: (s)=>s.shape === 'I' ? 0 : s.$depth / 2,
        $summary: (s, _state, ctx)=>{
            try {
                const walls = s.$walls.map((w)=>w.$summary);
                const dealerPrices = {
                    appliances: walls.reduce((acc, wall)=>acc + wall.dealerPrices.appliances, 0),
                    cabinetsAndParts: walls.reduce((acc, wall)=>acc + wall.dealerPrices.cabinetsAndParts, 0)
                };
                const retailPrices = {
                    appliances: walls.reduce((acc, wall)=>acc + wall.retailPrices.appliances, 0),
                    cabinetsAndParts: walls.reduce((acc, wall)=>acc + wall.retailPrices.cabinetsAndParts, 0)
                };
                const currentUserPrices = isDealer(ctx.sessionData) ? dealerPrices : retailPrices;
                return {
                    walls,
                    dealerPrices,
                    retailPrices,
                    currentUserPrices
                };
            } catch (error) {
                console.error(error);
            }
            // When there are some product definitions missing, we need to return a valid summary object.
            // This is needed to fail gracefully and allow access to the admin interface.
            return {
                walls: [],
                dealerPrices: {
                    appliances: 0,
                    cabinetsAndParts: 0
                },
                retailPrices: {
                    appliances: 0,
                    cabinetsAndParts: 0
                },
                currentUserPrices: {
                    appliances: 0,
                    cabinetsAndParts: 0
                }
            };
        },
        $width: (s)=>s.$wallB?.length ?? 0,
        $depth: (s)=>Math.max(s.$wallA?.length ?? 0, s.$wallC?.length ?? 0),
        $maxSideLength: (s)=>{
            return Math.max(s.$width, s.$depth);
        },
        $drawer33Door66Recipes: ()=>recipes.drawer33Door66,
        $sinkBaseCabinetRecipes: ()=>recipes.sinkBaseCabinetRecipes,
        $lynxEvoRecipes: (s)=>recipes.lynxEvo(s.fuelType),
        $lynxProfessionalRecipes: (s)=>recipes.lynxProfessionalGrill(s.fuelType),
        $lynxSedonaGrillRecipes: (s)=>recipes.lynxSedonaGrill(s.fuelType),
        $lynxProfessionalBurnerRecipes: (s)=>recipes.lynxProfessionalBurner(s.fuelType),
        $lynxSedonaBurnerRecipes: (s)=>recipes.lynxSedonaBurner(s.fuelType),
        $lynxSedonaHoodRecipes: ()=>recipes.lynxSedonaHood,
        $lynxSinkRecipes: ()=>recipes.lynxSink,
        $otherCookingItemRecipes: (s)=>recipes.otherCookingItems(s.fuelType),
        $fullDoorBaseRecipes: ()=>recipes.fullDoorBase,
        $trashPulloutBaseRecipes: ()=>recipes.trashPulloutBase,
        $threeDrawerBaseRecipes: ()=>recipes.threeDrawerBase,
        $fillerRecipes: ()=>recipes.fillers,
        $endPanelRecipes: ()=>recipes.endPanels,
        $refrigerationRecipes: ()=>recipes.refrigerationRecipes,
        $cabinetColorSwatches: ()=>MaterialSwatchMap,
        $appliancePrice: (s)=>{
            return round(s.$summary.currentUserPrices.appliances, 2);
        },
        $cabinetsAndPartsPrice: (s)=>{
            return round(s.$summary.currentUserPrices.cabinetsAndParts, 2);
        },
        $totalPrice: (s)=>{
            return round(s.$summary.currentUserPrices.appliances + s.$summary.currentUserPrices.cabinetsAndParts, 2);
        }
    }
});
export const roomInvariants = implementCustomInvariants({
    Room: {
        gasNotAvailable: (s)=>{
            return !s.$appliances.some(($appliance)=>$appliance.$fuelTypes.length > 0 && !$appliance.$fuelTypes.includes(s.fuelType));
        }
    }
});

//# sourceMappingURL=room.js.map